/* Contrato do FAQ/capacidades (ADR-0658 + ADR-0720). Conteúdo editorial gerido pelo backoffice
   (staff-write) e lido no app (read-only). Q&A com markdown, draft→published, i18n por linha
   (coluna `locale`). A CATEGORIA é bipartida (ADR-0720): `categoryKey` é um slug ESTÁVEL
   COMPARTILHADO pelos idiomas do tópico (agrupa/filtra/ordena) e `categoryLabel` é o rótulo POR
   IDIOMA (vive em cada linha (slug,locale), traduzível). A lista de gestão é PAGINADA POR TÓPICO via
   keyset (categoryKey,sortOrder,slug). Compartilhado entre admin-service e admin-web; o app reusa o
   `publicFaqEntryDto` via seu próprio client. */
import { z } from 'zod';

export const FAQ_STATUSES = ['draft', 'published'] as const;
export const faqStatusSchema = z.enum(FAQ_STATUSES);
export type FaqStatus = z.infer<typeof faqStatusSchema>;

/* Idiomas suportados na edição POR-TÓPICO (abas do form). O modelo aceita qualquer `locale`
   (i18n por linha), mas a UI edita este conjunto canônico. O grouping abaixo é genérico e
   preserva qualquer locale extra já persistido. */
export const FAQ_LOCALES = ['pt-BR', 'en-US'] as const;
export type FaqLocale = (typeof FAQ_LOCALES)[number];

/* Locale canônico do tópico: fonte dos metadados compartilhados (categoryKey/ordem) na visão
   agrupada e locale de fallback esperado na leitura. */
const FAQ_FALLBACK_LOCALE: FaqLocale = 'pt-BR';

const CATEGORY_LABEL_MAX_LEN = 60;
const CATEGORY_KEY_MAX_LEN = 60;
const QUESTION_MAX_LEN = 300;
const ANSWER_MAX_LEN = 20_000;
const SLUG_MAX_LEN = 80;
/* Slug estável (chave de seed idempotente) — kebab/underscore, sem espaços. */
const slugSchema = z
  .string()
  .min(1)
  .max(SLUG_MAX_LEN)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'faq_slug_invalid');

/* Slug ESTÁVEL da categoria (ADR-0720): compartilhado entre os idiomas do tópico; é a chave de
   agrupamento/filtro/ordenação. Mesmas regras do slug do tópico (kebab/underscore). */
const categoryKeySchema = z
  .string()
  .min(1)
  .max(CATEGORY_KEY_MAX_LEN)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'faq_category_key_invalid');

/* Rótulo POR IDIOMA da categoria (ADR-0720): texto traduzível exibido ao usuário. */
const categoryLabelSchema = z.string().min(1).max(CATEGORY_LABEL_MAX_LEN);

/** Entry completa (visão do admin — inclui draft + metadados). */
export const faqEntryDto = z.object({
  id: z.string().min(1),
  slug: slugSchema,
  categoryKey: categoryKeySchema,
  categoryLabel: categoryLabelSchema,
  question: z.string().min(1).max(QUESTION_MAX_LEN),
  answer: z.string().min(1).max(ANSWER_MAX_LEN),
  locale: z.string().min(2).max(10),
  status: faqStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FaqEntry = z.infer<typeof faqEntryDto>;

/** Visão PÚBLICA (app read-only) — sem status/timestamps de gestão. */
export const publicFaqEntryDto = z.object({
  slug: slugSchema,
  categoryKey: categoryKeySchema,
  categoryLabel: categoryLabelSchema,
  question: z.string().min(1).max(QUESTION_MAX_LEN),
  answer: z.string().min(1).max(ANSWER_MAX_LEN),
  sortOrder: z.number().int(),
});
export type PublicFaqEntry = z.infer<typeof publicFaqEntryDto>;

export const faqListDto = z.object({ entries: z.array(faqEntryDto) });
export type FaqList = z.infer<typeof faqListDto>;

export const publicFaqListDto = z.object({ entries: z.array(publicFaqEntryDto) });
export type PublicFaqList = z.infer<typeof publicFaqListDto>;

const LOCALE_DEFAULT = 'pt-BR';
const SORT_DEFAULT = 0;

/* Create: `.optional()` explícito nos campos com default (nunca `.partial()`). O backend aplica os
   defaults de locale/sortOrder/status. */
export const createFaqBody = z.object({
  slug: slugSchema,
  categoryKey: categoryKeySchema,
  categoryLabel: categoryLabelSchema,
  question: z.string().min(1).max(QUESTION_MAX_LEN),
  answer: z.string().min(1).max(ANSWER_MAX_LEN),
  locale: z.string().min(2).max(10).default(LOCALE_DEFAULT),
  status: faqStatusSchema.default('draft'),
  sortOrder: z.number().int().default(SORT_DEFAULT),
});
export type CreateFaqBody = z.infer<typeof createFaqBody>;

/* Update: objeto explícito com todos os campos `.optional()` (regra CLAUDE.md — nunca `.partial()`).
   `slug` NÃO é editável (é a identidade estável). `categoryKey` é editável (chave compartilhada);
   `categoryLabel` é editável por linha (rótulo do idioma). */
export const updateFaqBody = z.object({
  categoryKey: categoryKeySchema.optional(),
  categoryLabel: categoryLabelSchema.optional(),
  question: z.string().min(1).max(QUESTION_MAX_LEN).optional(),
  answer: z.string().min(1).max(ANSWER_MAX_LEN).optional(),
  locale: z.string().min(2).max(10).optional(),
  status: faqStatusSchema.optional(),
  sortOrder: z.number().int().optional(),
});
export type UpdateFaqBody = z.infer<typeof updateFaqBody>;

/* Body do seed (rota interna): lote de entries idempotente por (slug,locale). Reusa `createFaqBody`. */
export const seedFaqBody = z.object({ entries: z.array(createFaqBody).min(1) });
export type SeedFaqBody = z.infer<typeof seedFaqBody>;

/* ─── Paginação POR-TÓPICO (ADR-0720) ────────────────────────────────────────────────────────────
   A lista de gestão é paginada por TÓPICO (slug) com keyset sobre a ordenação de tópico
   (categoryKey,sortOrder,slug). `cursor`/`limit` seguem o padrão keyset das demais listas do
   backoffice (audit/companies). Os filtros da tela geral (busca/status/idioma) seguem client-side
   sobre as páginas carregadas (trade-off documentado em faq-page). `categoryKey` é o ÚNICO filtro
   server-side (ADR-0728): surfaces dedicadas (ex.: "Como Funciona" do publisher) listam só a sua
   categoria sem carregar o dataset inteiro. */
const FAQ_PAGE_LIMIT_DEFAULT = 50;
const FAQ_PAGE_LIMIT_MAX = 100;
export const listFaqQuery = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(FAQ_PAGE_LIMIT_MAX).default(FAQ_PAGE_LIMIT_DEFAULT),
  categoryKey: z.string().min(1).optional(),
});
export type ListFaqQuery = z.infer<typeof listFaqQuery>;

/* ─── Visão POR-TÓPICO (agrupada por slug) ────────────────────────────────────────────────────
   A tabela `faq_entries` guarda uma linha por (slug, locale). Um TÓPICO é o slug com suas
   variações de idioma. `categoryKey`/slug/sortOrder são COMPARTILHADOS pelo tópico (o write path
   grava o mesmo valor em cada linha de idioma); question/answer/status/categoryLabel são por idioma.
   `groupFaqEntriesByTopic` deriva os tópicos de `FaqEntry[]` (grouping puro), reusado pela UI e pelo
   admin-service (que também pagina no servidor). */

/** Uma variação de idioma dentro de um tópico agrupado (visão de gestão). */
export const faqTopicLocaleDto = z.object({
  id: z.string().min(1),
  locale: z.string().min(2).max(10),
  categoryLabel: categoryLabelSchema,
  question: z.string().min(1).max(QUESTION_MAX_LEN),
  answer: z.string().min(1).max(ANSWER_MAX_LEN),
  status: faqStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FaqTopicLocale = z.infer<typeof faqTopicLocaleDto>;

/** Tópico = slug + metadados compartilhados (categoryKey/ordem) + ≥1 variação por idioma (cada uma
    carrega o `categoryLabel` do seu locale). */
export const faqTopicDto = z.object({
  slug: slugSchema,
  categoryKey: categoryKeySchema,
  sortOrder: z.number().int(),
  locales: z.array(faqTopicLocaleDto).min(1),
});
export type FaqTopic = z.infer<typeof faqTopicDto>;

/* Página keyset de tópicos (`items` + `nextCursor` opaco) — mesma forma `{ items, nextCursor }`
   das demais listas keyset do backoffice. */
export const faqTopicPageDto = z.object({
  items: z.array(faqTopicDto),
  nextCursor: z.string().nullish(),
});
export type FaqTopicPage = z.infer<typeof faqTopicPageDto>;

/** Remoção homogênea por tópico: o slug identifica todas as traduções da linha visível. */
export const FAQ_BULK_MAX_TOPICS = 100;
export const bulkDeleteFaqTopicsBody = z.object({
  slugs: z
    .array(slugSchema)
    .min(1)
    .max(FAQ_BULK_MAX_TOPICS)
    .refine((slugs) => new Set(slugs).size === slugs.length, {
      message: 'duplicate_bulk_slugs',
    }),
  action: z.literal('deleteTopics'),
});
export type BulkDeleteFaqTopicsBody = z.infer<typeof bulkDeleteFaqTopicsBody>;

export const bulkDeleteFaqTopicsResponseDto = z.object({
  results: z.array(z.object({ slug: slugSchema, ok: z.boolean(), error: z.string().nullable() })),
});
export type BulkDeleteFaqTopicsResponse = z.infer<typeof bulkDeleteFaqTopicsResponseDto>;

/* Ordem de exibição dos idiomas (badges/abas): canônicos (FAQ_LOCALES) primeiro, resto ao fim. */
function faqLocaleRank(locale: string): number {
  const index = (FAQ_LOCALES as readonly string[]).indexOf(locale);
  return index === -1 ? FAQ_LOCALES.length : index;
}

/** Variação canônica do tópico: pt-BR se existir, senão a primeira por ordem de idioma. */
export function faqTopicPrimaryLocale(topic: FaqTopic): FaqTopicLocale {
  const preferred = topic.locales.find((l) => l.locale === FAQ_FALLBACK_LOCALE);
  if (preferred !== undefined) return preferred;
  const [first] = topic.locales;
  /* `faqTopicDto.locales` tem `.min(1)`: sempre há ≥1 variação — o undefined é impossível. */
  if (first === undefined) throw new Error('faq_topic_without_locale');
  return first;
}

/** Agrupa entries planas (uma por (slug,locale)) em tópicos por slug. `categoryKey`/ordem vêm da
    variação canônica; cada idioma carrega seu `categoryLabel`; idiomas ordenados (canônicos
    primeiro). Tópicos ordenados por (categoryKey, sortOrder, slug) — a MESMA ordenação do keyset
    server-side (ADR-0720). Puro — reusado por UI, admin-service e testes. */
export function groupFaqEntriesByTopic(entries: readonly FaqEntry[]): FaqTopic[] {
  const bySlug = new Map<string, FaqEntry[]>();
  for (const entry of entries) {
    const list = bySlug.get(entry.slug);
    if (list === undefined) bySlug.set(entry.slug, [entry]);
    else list.push(entry);
  }
  const topics: FaqTopic[] = [];
  for (const [slug, rows] of bySlug) {
    const sorted = [...rows].sort(
      (a, b) =>
        faqLocaleRank(a.locale) - faqLocaleRank(b.locale) || a.locale.localeCompare(b.locale),
    );
    const canonical = sorted.find((r) => r.locale === FAQ_FALLBACK_LOCALE) ?? sorted[0];
    if (canonical === undefined) continue;
    topics.push({
      slug,
      categoryKey: canonical.categoryKey,
      sortOrder: canonical.sortOrder,
      locales: sorted.map((r) => ({
        id: r.id,
        locale: r.locale,
        categoryLabel: r.categoryLabel,
        question: r.question,
        answer: r.answer,
        status: r.status,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      })),
    });
  }
  return topics.sort(
    (a, b) =>
      a.categoryKey.localeCompare(b.categoryKey) ||
      a.sortOrder - b.sortOrder ||
      a.slug.localeCompare(b.slug),
  );
}
